/* eslint-disable */

import React, { useRef, useState } from 'react'
import { Box, Typography, Button, TextField } from '@mui/material'
import SkeinIcon from './SkeinIcon'

interface ColorPickerProps {
  hexColor: string
  onChange: (hexColor: string) => void
  /** When true, hide the color/hex inputs - just show upload + canvas (for use inside color list) */
  minimal?: boolean
}

export default function ColorPicker({ hexColor, onChange, minimal }: ColorPickerProps) {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    const file = files && files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const imageUrl = e.target && e.target.result ? e.target.result as string : ''
        setUploadedImage(imageUrl)
        
        // Load image to canvas for color picking
        const img = new Image()
        img.onload = () => {
          imageRef.current = img
          if (canvasRef.current) {
            const canvas = canvasRef.current
            const ctx = canvas.getContext('2d')
            if (ctx) {
              // Keep canvas at full image resolution for accurate color picking
              canvas.width = img.width
              canvas.height = img.height
              ctx.drawImage(img, 0, 0)
            }
          }
        }
        img.src = imageUrl
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || !imageRef.current) return
    
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const clickY = event.clientY - rect.top
    
    // Calculate scale factor between displayed size and actual canvas size
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    
    // Convert click coordinates to canvas coordinates
    const x = Math.floor(clickX * scaleX)
    const y = Math.floor(clickY * scaleY)
    
    const ctx = canvas.getContext('2d')
    if (ctx) {
      const imageData = ctx.getImageData(x, y, 1, 1)
      const [r, g, b] = imageData.data
      const hex = `#${[r, g, b].map(x => {
        const h = x.toString(16)
        return h.length === 1 ? '0' + h : h
      }).join('')}`
      
      onChange(hex)
    }
  }

  const handleClear = () => {
    setUploadedImage(null)
    imageRef.current = null
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
      }
    }
  }

  return (
    <Box sx={{ mb: minimal ? 0 : 2 }}>
      {!minimal && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              type="color"
              value={hexColor}
              onChange={(e) => onChange(e.target.value)}
              sx={{ width: 80 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Hex"
              value={hexColor}
              onChange={(e) => onChange(e.target.value)}
              sx={{ flex: 1 }}
              placeholder="#000000"
            />
            <SkeinIcon color={hexColor} size={100} />
          </Box>
        </>
      )}
      
      <Box sx={{ mb: minimal ? 0 : 2 }}>
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id="color-picker-upload"
          type="file"
          onChange={handleImageUpload}
        />
        <label htmlFor="color-picker-upload">
          <Button variant="outlined" component="span" fullWidth sx={{ mb: minimal ? 1 : 2 }}>
            Upload Photo to Pick Color
          </Button>
          {!minimal && (
            <Typography variant="body2" color="text.secondary">Use a photo from their website if possible!</Typography>
          )}
        </label>
        
        {uploadedImage && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">
                Click on the image to set this color
              </Typography>
              <Button size="small" onClick={handleClear}>
                Clear
              </Button>
            </Box>
            <Box sx={{ 
              border: '2px solid #ccc', 
              borderRadius: 1, 
              overflow: 'hidden',
              display: 'inline-block',
              cursor: 'crosshair',
              maxWidth: '100%'
            }}>
              <canvas
                ref={canvasRef}
                onClick={handleCanvasClick}
                style={{ 
                  maxWidth: '600px',
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
